import React, { useState, useCallback } from "react";
import { Container } from "reactstrap";
import { Button, Col, Drawer, Row } from "rsuite";
import sortUpDownIcon from "../../assets/img/sort-updown.svg";
import sortAscIcon from "../../assets/img/sort-asc.svg";
import sortDescIcon from "../../assets/img/sort-desc.svg";
import PlusIcon from "../../assets/img/plus-circle.png";
import MinusIcon from "../../assets/img/minus-circle.png";
import Table from "../../components/common/table/Table";
import SummaryTooltipCell from "../../components/common/SummaryTooltipCell";
import TruncatedText from "../../components/common/TruncatedText";
import { usePropertyStore } from "../../contexts/PropertyStoreContext";
import ResReportForm from "./resReportForm";
import UploadPanel from "./uploadPms";
import ResReportFilters from "./ResReportFilters";
import ReservationDailyBreakdown from "./ReservationDailyBreakdown";
import {
  DEFAULT_COLUMNS,
  INIT_FORM_VALUE,
  DEFAULT_PAGE_SIZE,
  cancelledData,
  calculateDailyBreakdownHeight,
  splitPromoRatePlans,
} from "./utils";
import { SORTABLE_COLUMNS } from "./constants";
import "./resReport.scss";
import { dateFormatter, renderDateRange } from "../../utils/dateUtils";
import { P, usePermission } from "../../rbac";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import useResReportForm from "./hooks/useResReportForm";
import useReservationData from "./hooks/useReservationData";
import useInlineEdit from "./hooks/useInlineEdit";
import useDailyRates from "./hooks/useDailyRates";

const { Column, HeaderCell, Cell } = Table;

dayjs.extend(utc);
dayjs.extend(timezone);

const ResReport = () => {
  const { selectedProperty, isPilotHotel } = usePropertyStore();
  const { allowed: canEditReservations } = usePermission(P.Reservations.Edit);
  const { allowed: canViewPmsId } = usePermission(P.Reservations.View);
  const { allowed: canRepushVcc } = usePermission(P.Reservations.VccRepush);

  const [columns, setColumns] = useState(
    DEFAULT_COLUMNS.filter(
      (col) =>
        !col.isHidden &&
        (canViewPmsId || col.dataKey !== "pmsId") &&
        (canRepushVcc || col.dataKey !== "repushVcc")
    )
  );

  const [sortState, setSortState] = useState({
    sortBy: null,
    sortDirection: null,
  });

  const {
    isEdit,
    setIsEdit,
    isDrawerOpen,
    setIsDrawerOpen,
    formValue,
    setFormValue,
    filters,
    setFilters,
    filteredFields,
    isUploadSidePanelOpen,
    setIsUploadSidePanelOpen,
    isOffline,
    formSubmitted,
    searchTrigger,
  } = useResReportForm({ selectedProperty });

  const {
    isLoading,
    isExportLoading,
    reservations,
    setReservations,
    promoRatePlans,
    demandClients,
    reservationsPage,
    setReservationsPage,
    searchSequence,
    timeZoneRef,
    getReservationReport,
    getReservationReportRef,
    exportReservation,
    handleSortChange,
    onPageChange,
  } = useReservationData({
    selectedProperty,
    formValue,
    filters,
    isEdit,
    isDrawerOpen,
    canRepushVcc,
    columns,
    sortState,
    setSortState,
    searchTrigger,
  });

  const { renderInlineEditCell } = useInlineEdit({
    reservations,
    setReservations,
    isOffline,
    canEditReservations,
    getReservationReportRef,
  });

  const { expandedRowKeys, dailyRatesByCode, handleToggleExpand } =
    useDailyRates({ searchSequence });

  const handleFilterChange = useCallback(
    (updater) => {
      setFilters(updater);
      setReservationsPage((prev) => ({ ...prev, number: 0 }));
    },
    [setFilters, setReservationsPage]
  );

  const renderSortIcon = (field) => {
    const isActive = sortState.sortBy === field;
    const iconSrc = isActive
      ? sortState.sortDirection === "ASC"
        ? sortAscIcon
        : sortDescIcon
      : sortUpDownIcon;
    return (
      <button
        type="button"
        className={`sort-btn ${isActive ? "active" : ""}`}
        onClick={() => handleSortChange(field)}
        aria-label={isActive ? `Sorted ${sortState.sortDirection}` : "Sort"}
      >
        <img src={iconSrc} alt="" width="16" height="16" />
      </button>
    );
  };

  const renderCellContent = (rowData, dataKey) => {
    if (
      [
        "bookingCreationDate",
        "arrival",
        "departure",
        "cancellationDate",
        "cancellationCutOffLocal",
      ].includes(dataKey)
    ) {
      const rawDate = dateFormatter(
        rowData[dataKey],
        dataKey === "cancellationDate" || dataKey === "cancellationCutOffLocal"
      );
      return <TruncatedText text={rawDate} placeholder="" />;
    }
    if (dataKey === "pmsId" || dataKey === "cxlId") {
      return renderInlineEditCell(rowData, dataKey);
    }
    if (dataKey === "meshed") {
      return rowData.meshed ? "Yes" : "No";
    }
    if (dataKey === "promoRatePlan") {
      return (
        <SummaryTooltipCell
          items={splitPromoRatePlans(rowData.promoRatePlan)}
          emptyText=""
        />
      );
    }
    // status and repushVcc are pre-rendered JSX (badge / button), not plain
    // overflow-prone text, so they keep their native-title wrapper.
    if (dataKey === "status") {
      return <div title={rowData["statusValue"]}>{rowData[dataKey]}</div>;
    }
    if (dataKey === "repushVcc") {
      return <div title="Repush VCC">{rowData[dataKey]}</div>;
    }
    return <TruncatedText text={rowData[dataKey]} placeholder="" />;
  };

  const searchedData = filteredFields.map((field, idx) => {
    const step = idx + 1;
    return (
      <Col key={step} className="searched-field">
        <div className="icon">{<field.icon />}</div>
        <div className="data">
          <span className="label">{field.label}</span>
          {["Check In Dates", "Check Out Dates", "Booking Dates"].includes(
            field.label
          ) ? (
            <span className="field">{renderDateRange(field.value)}</span>
          ) : (
            <span className="field">{field.value}</span>
          )}
        </div>
      </Col>
    );
  });

  const formProps = {
    isLoading,
    isOffline,
    formSubmitted,
    cancelledData,
    setReservationsPage,
    initFormValue: INIT_FORM_VALUE,
    formValue,
    setFormValue,
  };

  return (
    <Container
      id="res-report"
      data-heap-id="res-report"
      fluid
      className="res-container"
    >
      {isEdit ? (
        <ResReportForm {...formProps} />
      ) : (
        <>
          <Drawer
            placement="top"
            open={isDrawerOpen}
            closeButton={false}
            onClose={() => setIsEdit(false)}
          >
            <Drawer.Body className="res-drawer">
              <ResReportForm
                {...formProps}
                setIsDrawerOpen={setIsDrawerOpen}
                source="drawer"
              />
            </Drawer.Body>
          </Drawer>

          <p className="page-header"> Reservation Report</p>
          <Row>
            {searchedData}
            <Col xs={2}>
              <Button appearance="ghost" onClick={() => setIsDrawerOpen(true)}>
                Edit search
              </Button>
            </Col>
          </Row>
          <div className="reservation-data">
            <div className="reservation-data-body">
              <ResReportFilters
                propertyId={selectedProperty?.propertyId}
                isOffline={isOffline}
                isUploadSidePanelOpen={isUploadSidePanelOpen}
                demandClients={demandClients}
                promoRatePlans={promoRatePlans}
                exportReservation={exportReservation}
                isExportLoading={isExportLoading}
                setIsUploadSidePanelOpen={setIsUploadSidePanelOpen}
                setFilters={handleFilterChange}
                setColumns={setColumns}
                timeZoneId={timeZoneRef.current}
                isPilotHotel={isPilotHotel}
              />
              <Table
                data={reservations}
                loading={isLoading}
                paginated
                onPageChange={onPageChange}
                pageSizeOptions={[10, 20, 30]}
                total={reservationsPage.totalElements}
                defaultPageSize={DEFAULT_PAGE_SIZE}
                currentPage={reservationsPage.number + 1}
                rowDisabledHandler={(rowData, index) =>
                  index > -1 && !rowData?.active
                }
                rowKey="htConfirmationCode"
                expandedRowKeys={expandedRowKeys}
                shouldUpdateScroll={false}
                rowExpandedHeight={(rowData) =>
                  calculateDailyBreakdownHeight(
                    rowData,
                    dailyRatesByCode[rowData?.htConfirmationCode]
                  )
                }
                renderRowExpanded={(rowData) => (
                  <ReservationDailyBreakdown
                    rowData={rowData}
                    dailyRatesEntry={
                      dailyRatesByCode[rowData.htConfirmationCode]
                    }
                  />
                )}
              >
                <Column width={40} align="center" fixed>
                  <HeaderCell />
                  <Cell>
                    {(rowData) => {
                      const isExpanded = expandedRowKeys.includes(
                        rowData.htConfirmationCode
                      );
                      return (
                        <button
                          type="button"
                          className="expand-btn"
                          data-heap-id="res-report-daily-breakdown-toggle"
                          onClick={() =>
                            handleToggleExpand(rowData.htConfirmationCode)
                          }
                          aria-label={
                            isExpanded
                              ? "Collapse daily breakdown"
                              : "Expand daily breakdown"
                          }
                        >
                          <img
                            src={isExpanded ? MinusIcon : PlusIcon}
                            alt=""
                            className="expand-btn-icon"
                          />
                        </button>
                      );
                    }}
                  </Cell>
                </Column>
                {columns.map((column) => {
                  const { label, dataKey, width, header, ...rest } = column;
                  const displayLabel = label || header;
                  return (
                    <Column key={label} width={width}>
                      <HeaderCell>
                        <div className="header-cell" title={displayLabel}>
                          {displayLabel}
                          {SORTABLE_COLUMNS.includes(dataKey) &&
                            renderSortIcon(dataKey)}
                        </div>
                      </HeaderCell>
                      <Cell dataKey={dataKey} {...rest}>
                        {(rowData) => renderCellContent(rowData, dataKey)}
                      </Cell>
                    </Column>
                  );
                })}
              </Table>
              <UploadPanel
                isUploadSidePanelOpen={isUploadSidePanelOpen}
                exportReservation={exportReservation}
                isExportLoading={isExportLoading}
                getReservationReport={getReservationReport}
                setIsUploadSidePanelOpen={setIsUploadSidePanelOpen}
                className={`upload-panel ${isUploadSidePanelOpen ? "show" : ""}`}
              />
            </div>
          </div>
        </>
      )}
    </Container>
  );
};

export default ResReport;
